import * as React from "react";
import { useFormContext } from "react-hook-form";
import { Phone, Mail, MapPin, Clock, Send, Wrench, Gauge } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
const ContactForm = () => {
  const {
    control,
    handleSubmit: submitForm,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    resetForm
  } = useWvcForm();
  const {
    handleSubmit: rhfSubmit
  } = useFormContext();
  const onSubmit = async (values: any) => {
    try {
      await submitForm();
    } catch (error) {
      console.error("Form submission error", error);
    }
  };
  if (isSubmitted && !submitError) {
    return <div data-section-id="28674" className="flex flex-col items-center justify-center h-full min-h-[300px] text-center p-8 space-y-4 bg-accent/5 rounded-xl border border-accent/20 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-accent/10 flex items-center justify-center mb-2">
          <Send className="size-8 text-accent" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-primary">Booking Requested</h3>
        <p className="text-secondary max-w-xs mx-auto font-sans">{successMessage}</p>
        <Button onClick={() => resetForm()} variant="outline" className="mt-4 rounded-lg">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={rhfSubmit(onSubmit)} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/90 uppercase tracking-wider text-xs font-bold font-sans">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Your full name" {...field} className="bg-background border-input focus-visible:border-accent rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/90 uppercase tracking-wider text-xs font-bold font-sans">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="your@email.com" {...field} className="bg-background border-input focus-visible:border-accent rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/90 uppercase tracking-wider text-xs font-bold font-sans">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Your mobile, e.g. +357 9x xxx xxx" {...field} className="bg-background border-input focus-visible:border-accent rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/90 uppercase tracking-wider text-xs font-bold font-sans">Preferred Location</FormLabel>
              <FormControl>
                <Input placeholder="Limassol or Nicosia" {...field} className="bg-background border-input focus-visible:border-accent rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary/90 uppercase tracking-wider text-xs font-bold font-sans">How can we help?</FormLabel>
            <FormControl>
              <textarea className={cn("flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-base shadow-sm placeholder:text-secondary/50 focus-visible:outline-none focus-visible:border-accent focus-visible:ring-1 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-50 md:text-sm transition-colors resize-y font-sans")} placeholder="Tell us about your symptoms or treatment interest..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm font-medium font-sans">
          {submitError}
        </div>}

      <FormField control={control} name="to" render={({
      field
    }) => <FormItem className="hidden">
            <FormControl>
              <Input type="hidden" {...field} />
            </FormControl>
          </FormItem>} />

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent hover:bg-accent/90 text-white font-bold tracking-wide uppercase h-12 text-base shadow-lg shadow-accent/20 rounded-lg transition-transform hover:scale-[1.02] active:scale-[0.98]">
        {isSubmitting ? <>
            <Gauge className="mr-2 size-5 animate-spin" />
            Processing...
          </> : "REQUEST AN APPOINTMENT"}
      </Button>
    </Form>;
};
export default function ReachOutToTheShop() {
  return <section id="book-your-session" className="bg-background text-foreground py-20 relative overflow-hidden">
      
      <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
      backgroundImage: `radial-gradient(circle at 2px 2px, #3B8FD4 1px, transparent 0)`,
      backgroundSize: '48px 48px'
    }} />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-serif font-medium tracking-tight text-primary">Contact Bodywerks</h1>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          
          <div className="space-y-10">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-accent/10 border border-accent/20 text-accent text-xs font-bold uppercase tracking-widest font-sans">
                <Wrench className="size-3" />
                <span>Contact & Book</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight text-primary leading-tight">
                Your Path to Wellness <br />
                <span className="text-accent">Starts Here</span>
              </h2>
              <p className="text-lg text-secondary max-w-md leading-relaxed font-sans">
                Whether you're seeking relief from chronic pain or looking to optimize your physical performance, Bodywerks provides expert care tailored to your needs.
              </p>
            </div>

            <div className="grid gap-6">
              
              <Link to="tel:+35796117140" className="group flex items-center gap-6 p-6 rounded-lg border border-border bg-white hover:border-accent/50 hover:shadow-xl transition-all duration-300">
                <div className="size-14 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-colors duration-300 shrink-0">
                  <Phone className="size-7 text-accent group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary uppercase tracking-wider mb-1 font-sans">Limassol Clinic</p>
                  <p className="text-xl md:text-2xl font-serif font-bold text-primary group-hover:text-accent transition-colors">+357 96 117140&nbsp;</p>
                </div>
              </Link>

              
              <Link to="tel:+35799433537" className="group flex items-center gap-6 p-6 rounded-lg border border-border bg-white hover:border-accent/50 hover:shadow-xl transition-all duration-300">
                <div className="size-14 rounded-lg bg-accent/10 flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-colors duration-300 shrink-0">
                  <Phone className="size-7 text-accent group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary uppercase tracking-wider mb-1 font-sans">Nicosia Clinic</p>
                  <p className="text-xl md:text-2xl font-serif font-bold text-primary group-hover:text-accent transition-colors">+357 99 433537&nbsp;</p>
                </div>
              </Link>

              
              <div className="flex items-center gap-6 p-6 rounded-lg border border-border bg-white/50">
                <div className="size-14 rounded-lg bg-primary/5 flex items-center justify-center shrink-0">
                  <MapPin className="size-7 text-secondary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary uppercase tracking-wider mb-1 font-sans">Locations</p>
                  <p className="text-lg font-semibold text-primary font-sans">Limassol & Nicosia, Cyprus</p>
                </div>
              </div>

              
              <div className="flex items-center gap-6 p-6 rounded-lg border border-border bg-white/50">
                <div className="size-14 rounded-lg bg-primary/5 flex items-center justify-center shrink-0">
                  <Clock className="size-7 text-secondary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary uppercase tracking-wider mb-1 font-sans">Clinic Hours</p>
                  <p className="text-lg font-semibold text-primary font-sans">Mon & Wed: 4:00 PM - 7:30 PM · Tue & Thu: 9:30 AM - 12:30 PM</p>
                  <p className="text-sm text-secondary font-sans">Fri: 3:00 PM - 6:30 PM · Sat & Sun: closed</p>
                </div>
              </div>
            </div>
          </div>

          
          <div className="lg:mt-10">
            <Card className="border border-border bg-white backdrop-blur-sm shadow-2xl relative overflow-hidden rounded-lg">
              
              <div className="absolute top-0 left-0 w-12 h-1 bg-accent z-20" />
              
              <CardHeader className="pb-4 border-b border-border/50 bg-white/80">
                <CardTitle className="text-2xl font-serif font-medium text-primary flex items-center gap-3">
                  <span className="w-1.5 h-6 bg-accent rounded-lg inline-block" />
                  Request a Consultation
                </CardTitle>
                <CardDescription className="pl-4.5 pt-1 font-sans text-secondary">
                  Fill out the details below and our team will get back to you shortly.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-6 md:p-8">
                <FormProvider formKey="booking_form_contact" formId="booking_form_contact" sectionName="contact-section" defaultValues={{
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
                to: "bodywerkscy@gmail.com"
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="REQUEST AN APPOINTMENT" successMessage="Thank you. We have received your request and will contact you shortly to confirm your booking." errorMessage="Transmission failed. Please check your connection and try again.">
                  <ContactForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}